export type UserStateKnownItemKey = "profile" | "preferences" | "settings" | "emailAlerts" | "marketingConsent";
export type UserStateSavedJobKey = `savedJob#${string}`;
export type UserStateViewedJobKey = `viewedJob#${string}`;
export type UserStateAppliedJobKey = `appliedJob#${string}`;
export type UserStateBrowsedJobKey = `browsedJob#${string}`;
export type UserStateFeedbackKey = `feedback#${string}`;
export type UserStateSavedSearchKey = `savedSearch#${string}`;
export type UserStateItemKey =
  | UserStateKnownItemKey
  | UserStateSavedJobKey
  | UserStateViewedJobKey
  | UserStateAppliedJobKey
  | UserStateBrowsedJobKey
  | UserStateFeedbackKey
  | UserStateSavedSearchKey;

export interface UserStateEntry<TData extends Record<string, unknown> = Record<string, unknown>> {
  data: TData;
  version: number;
  updatedAt: string | null;
}

export interface UserStateStoredItem<TData extends Record<string, unknown> = Record<string, unknown>>
  extends UserStateEntry<TData> {
  userId: string;
  itemKey: UserStateItemKey;
  updatedAt: string;
}

export type UserStateMap = Record<string, UserStateEntry>;

export const USER_STATE_INITIAL_VERSION = 1;
export const USER_STATE_CONFLICT_CODE = "USER_STATE_CONFLICT";

export function isSavedJobKey(itemKey: string): itemKey is UserStateSavedJobKey {
  return itemKey.startsWith("savedJob#") && itemKey.length > "savedJob#".length;
}

export function isViewedJobKey(itemKey: string): itemKey is UserStateViewedJobKey {
  return itemKey.startsWith("viewedJob#") && itemKey.length > "viewedJob#".length;
}

export function isAppliedJobKey(itemKey: string): itemKey is UserStateAppliedJobKey {
  return itemKey.startsWith("appliedJob#") && itemKey.length > "appliedJob#".length;
}

export function isBrowsedJobKey(itemKey: string): itemKey is UserStateBrowsedJobKey {
  return itemKey.startsWith("browsedJob#") && itemKey.length > "browsedJob#".length;
}

export function isFeedbackKey(itemKey: string): itemKey is UserStateFeedbackKey {
  return itemKey.startsWith("feedback#") && itemKey.length > "feedback#".length;
}

export function isSavedSearchKey(itemKey: string): itemKey is UserStateSavedSearchKey {
  return itemKey.startsWith("savedSearch#") && itemKey.length > "savedSearch#".length;
}

export function isUserStateItemKey(itemKey: string): itemKey is UserStateItemKey {
  if (
    itemKey === "profile" ||
    itemKey === "preferences" ||
    itemKey === "settings" ||
    itemKey === "emailAlerts" ||
    itemKey === "marketingConsent"
  ) {
    return true;
  }
  return isSavedJobKey(itemKey) || isViewedJobKey(itemKey) || isAppliedJobKey(itemKey) || isBrowsedJobKey(itemKey) || isFeedbackKey(itemKey) || isSavedSearchKey(itemKey);
}

export function createDefaultUserStateEntry(itemKey: UserStateItemKey): UserStateEntry {
  switch (itemKey) {
    case "profile":
      return {
        data: {},
        version: 0,
        updatedAt: null,
      };
    case "preferences":
      return {
        data: {},
        version: 0,
        updatedAt: null,
      };
    case "settings":
      return {
        data: {},
        version: 0,
        updatedAt: null,
      };
    default:
      return {
        data: {},
        version: 0,
        updatedAt: null,
      };
  }
}

